import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import { events } from '@/mocks/events';
import { materialMeta, statusStyle } from '@/utils/content';
import EventCard from '@/components/feature/EventCard';

export default function EventDetail() {
  const { slug } = useParams<{ slug: string }>();
  const idx = events.findIndex((e) => e.slug === slug);
  const ev = idx >= 0 ? events[idx] : null;

  if (!ev) {
    return (
      <>
        <Seo title="未找到档案 - 69爆料" description="你查找的事件档案不存在，请返回证据库继续检索。" canonicalPath="/archive" />
        <section className="max-w-3xl mx-auto px-4 text-center py-24">
          <div className="mx-auto w-16 h-16 inline-flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl">
            <i className="ri-file-warning-line"></i>
          </div>
          <h1 className="mt-5 font-display font-black text-foreground-950 text-2xl">这份档案不存在或已合并</h1>
          <p className="mt-3 text-foreground-500 text-sm">你访问的事件档案可能已并入其他条目或尚未收录。</p>
          <Link
            to="/archive"
            className="mt-6 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full px-6 py-2.5 text-sm transition-colors"
          >
            <i className="ri-arrow-left-line"></i>返回证据库
          </Link>
        </section>
      </>
    );
  }

  const prev = idx > 0 ? events[idx - 1] : null;
  const next = idx < events.length - 1 ? events[idx + 1] : null;
  const related = events.filter((e) => ev.related.includes(e.slug)).slice(0, 3);
  const personList = Array.from(new Set([...ev.persons]));

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '档案首页', item: 'https://kpsdk.cn/' },
          { '@type': 'ListItem', position: 2, name: '实锤证据库', item: 'https://kpsdk.cn/archive' },
          { '@type': 'ListItem', position: 3, name: ev.title },
        ],
      },
      {
        '@type': 'Article',
        headline: ev.title,
        description: ev.summary,
        image: ev.cover,
        datePublished: ev.time,
        author: { '@type': 'Organization', name: '69爆料' },
        publisher: { '@type': 'Organization', name: '69爆料' },
        mainEntityOfPage: `https://kpsdk.cn/event/${ev.slug}`,
      },
    ],
  };

  return (
    <>
      <Seo
        title={`${ev.title} - 69爆料证据档案`}
        description={`${ev.title}：${ev.summary}。已整理${ev.materials.length}项素材并标注来源，支持查看完整补档时间线与反锤材料。`}
        keywords={`69爆料,${ev.category},${ev.persons[0] || ''},实锤证据,素材整理,辟谣反锤`}
        canonicalPath={`/event/${ev.slug}`}
        jsonLd={articleJsonLd}
      />

      {/* 档案头图 */}
      <section className="relative overflow-hidden">
        <img
          src={ev.cover}
          alt={`${ev.title} · 档案素材封面`}
          title={ev.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/35 to-foreground-950/20"></div>
        <div className="relative max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10 pt-8 md:pt-12 pb-12 md:pb-16 flex flex-col min-h-[380px] md:min-h-[460px]">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-200" aria-label="面包屑">
            <Link to="/" className="hover:text-secondary-200 transition-colors">档案首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/archive" className="hover:text-secondary-200 transition-colors">实锤证据库</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100 line-clamp-1">{ev.title}</span>
          </nav>

          <div className="mt-auto pt-10">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-background-50 bg-background-950/50 px-3 py-1 rounded-full">
                {ev.category}
              </span>
              <span className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full ${statusStyle[ev.status]}`}>
                <i className="ri-verified-badge-fill"></i>
                {ev.status}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-background-100 bg-background-950/40 px-3 py-1 rounded-full">
                <i className="ri-time-line"></i>收录于 {ev.time}
              </span>
            </div>
            <h1 className="mt-4 font-display font-black text-background-50 text-3xl md:text-5xl leading-tight max-w-4xl">
              {ev.title}
            </h1>
            <p className="mt-3 text-background-200 text-sm md:text-lg max-w-2xl leading-relaxed">{ev.tagline}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-100">
              {personList.map((p) => (
                <span key={p} className="inline-flex items-center gap-1.5">
                  <i className="ri-user-line text-secondary-300"></i>{p}
                </span>
              ))}
              <span className="ml-auto inline-flex items-center gap-1.5 text-primary-100">
                <i className="ri-fire-line"></i>热度 {ev.heat}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 主体 */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {/* 事件经过 */}
          <article>
            <h2 className="font-display font-bold text-foreground-950 text-xl md:text-2xl flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-primary-100 text-primary-600"><i className="ri-focus-3-line"></i></span>
              事件经过
            </h2>
            <div className="mt-4 space-y-4 text-foreground-700 leading-relaxed text-[15px] md:text-base">
              {ev.paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? 'first-letter:float-left first-letter:font-display first-letter:text-5xl first-letter:leading-[0.85] first-letter:text-primary-600 first-letter:mr-2 first-letter:font-black first-letter:pr-1' : ''}>
                  {p}
                </p>
              ))}
            </div>
          </article>

          {/* 证据素材 */}
          <div className="mt-10">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-display font-bold text-foreground-950 text-xl md:text-2xl flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-primary-100 text-primary-600"><i className="ri-archive-drawer-line"></i></span>
                证据素材清单（{ev.materials.length}）
              </h2>
              <span className="text-xs text-foreground-500">已对明显隐私信息做脱敏处理</span>
            </div>
            <ul className="mt-5 divide-y divide-background-200 border-y border-background-200 bg-background-50">
              {ev.materials.map((m) => {
                const mm = materialMeta[m.type];
                return (
                  <li key={m.title} className="py-4 flex gap-4">
                    <span className={`inline-flex items-center justify-center w-11 h-11 rounded-lg shrink-0 text-lg ${mm.color}`}>
                      <i className={mm.icon}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-medium text-foreground-950">{m.title}</h3>
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${m.verified ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-900'}`}>
                          {m.verified ? <i className="ri-check-double-line"></i> : <i className="ri-time-line"></i>}
                          {m.verified ? '已核来源' : '待多方核验'}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{m.note}</p>
                      <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1"><i className="ri-community-line"></i>{mm.label}</span>
                        <span className="inline-flex items-center gap-1"><i className="ri-file-user-line"></i>来源：{m.source}</span>
                        <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i>{m.time}</span>
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 flex items-start gap-2 text-xs text-foreground-500 leading-relaxed">
              <i className="ri-information-line text-primary-500 mt-0.5 shrink-0"></i>
              以上素材仅供公开信息整理与比对参考，本站不对材料真实性作担保，请结合来源与第三方凭证自行核验。
            </p>
          </div>

          {/* 时间线 */}
          <div className="mt-10">
            <h2 className="font-display font-bold text-foreground-950 text-xl md:text-2xl flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-accent-100 text-accent-700"><i className="ri-time-line"></i></span>
              补档时间线
            </h2>
            <ol className="mt-6 border-l-2 border-background-200 ml-3 space-y-6">
              {ev.timeline.map((t, i) => (
                <li key={i} className="relative pl-6">
                  <span className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-4 border-background-50 ${i === ev.timeline.length - 1 ? 'bg-primary-500' : 'bg-accent-500'}`}></span>
                  <p className="text-xs text-foreground-500 font-medium">{t.time}</p>
                  <p className="mt-1 text-sm text-foreground-700 leading-relaxed">{t.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* 侧栏 */}
        <aside className="space-y-6 lg:sticky lg:top-24 self-start">
          <div className="bg-background-100/70 border border-background-200 rounded-lg p-5">
            <h2 className="font-display font-bold text-foreground-950">档案信息</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">档案状态</dt>
                <dd className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${statusStyle[ev.status]}`}>{ev.status}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">所属分类</dt>
                <dd className="text-foreground-950 font-medium">{ev.category}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">收录素材</dt>
                <dd className="text-foreground-950 font-medium">{ev.materials.length} 项</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">收录时间</dt>
                <dd className="text-foreground-950 font-medium">{ev.time}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">热度</dt>
                <dd className="inline-flex items-center gap-1 text-primary-600 font-medium"><i className="ri-fire-fill"></i>{ev.heat}</dd>
              </div>
            </dl>
            <div className="mt-4 pt-4 border-t border-background-200">
              <h3 className="text-xs text-foreground-500 mb-2">素材类型分布</h3>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(new Set(ev.materials.map((m) => m.type))).map((t) => {
                  const mm = materialMeta[t];
                  return (
                    <span key={t} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${mm.color}`}>
                      <i className={mm.icon}></i>{mm.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-foreground-950 text-background-200 rounded-lg p-5">
            <h3 className="font-display font-bold text-background-50 flex items-center gap-2">
              <i className="ri-shield-check-line text-accent-400"></i>中立声明
            </h3>
            <p className="mt-3 text-xs leading-relaxed text-background-300">
              本站为「材料归拢 + 标注来源」的信息整理站，不预设立场、不代替司法与监管定性。档案状态会随反锤与新素材持续更新，请理性吃瓜。
            </p>
            <Link
              to="/about"
              className="mt-4 inline-flex items-center gap-1.5 text-accent-300 hover:text-accent-200 text-sm transition-colors"
            >
              了解我们的原则 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="bg-background-50 border border-background-200 rounded-lg p-5 text-center">
            <p className="text-sm text-foreground-600">你也掌握相关线索？</p>
            <Link
              to="/about#contact"
              className="mt-3 inline-flex items-center justify-center gap-2 w-full bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full px-4 py-2.5 text-sm transition-colors"
            >
              <i className="ri-megaphone-line"></i>补充证据
            </Link>
          </div>
        </aside>
      </section>

      {/* 上下篇 + 相关 */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10 pb-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {prev && (
            <Link to={`/event/${prev.slug}`} className="group flex items-center gap-3 border border-background-200 hover:border-primary-300 bg-background-100/50 rounded-lg p-4 transition-colors">
              <i className="ri-arrow-left-s-line text-foreground-400 group-hover:text-primary-600"></i>
              <div className="min-w-0">
                <p className="text-[11px] text-foreground-500">上一篇档案</p>
                <p className="text-sm text-foreground-800 line-clamp-1 group-hover:text-primary-700">{prev.title}</p>
              </div>
            </Link>
          )}
          {next && (
            <Link to={`/event/${next.slug}`} className="group sm:justify-end flex items-center gap-3 border border-background-200 hover:border-primary-300 bg-background-100/50 rounded-lg p-4 transition-colors">
              <div className="min-w-0 text-right">
                <p className="text-[11px] text-foreground-500">下一篇档案</p>
                <p className="text-sm text-foreground-800 line-clamp-1 group-hover:text-primary-700">{next.title}</p>
              </div>
              <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-600"></i>
            </Link>
          )}
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <div className="flex items-end justify-between gap-3">
              <h2 className="font-display font-black text-foreground-950 text-xl md:text-2xl">相关档案</h2>
              <Link to="/archive" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
                更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((e) => (
                <EventCard key={e.slug} ev={e} />
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}